import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { InstrumentChartCardInput, ModelStrategyInstrumentOut } from '../charts';
import { ChartConfiguration } from 'chart.js';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { InstrumentDropdownInput } from '../instrument-chart-card/instrument-chart-dropdown/instrument-dropdown-input';
import { instrumentCategoryColors, randomColors } from '../../constants';
import { externalTooltipHandler } from '../custom-hover-tooltip';
import { lighterColor } from '../../functions';

// todo missing translations for dynamic instruments from API
const cashRegionName = 'Střední a východní Evropa';

@Component({
  selector: 'libero-region-chart-card',
  templateUrl: './region-chart-card.component.html',
  styleUrls: ['./region-chart-card.component.scss'],
  standalone: false
})
export class RegionChartCardComponent implements OnInit, OnChanges {
  @Input() public cardData?: InstrumentChartCardInput;

  public doughnutChartData?: ChartConfiguration['data'];
  public dropdownData: InstrumentDropdownInput [] = [];

  public doughnutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        position: 'nearest',
        external: (context) => externalTooltipHandler(context, 'instrumentChart'),
      },
    },
  };

  public showLabels = false;

  private datasetsLabels: string[] = [];
  public datasetsColors: string[] = [
    instrumentCategoryColors['EQUITY'],
    instrumentCategoryColors['BOND'],
    instrumentCategoryColors['ALTERNATIVE'],
    instrumentCategoryColors['COMODITY'],
    instrumentCategoryColors['MONEY'],
    instrumentCategoryColors['REAL_ESTATE'],
    instrumentCategoryColors['LEVERAGED'],
    instrumentCategoryColors['CRYPTOCURRENCY'],
    instrumentCategoryColors['MULTI_ASSET'],
    instrumentCategoryColors['CASH'],
    ...randomColors
  ];

  public constructor(
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
  ) { }

  public ngOnInit(): void {
    this.renderData();
  }

  private renderData(): void {
    if(!this.cardData?.chartData || this.cardData?.chartData.length < 1) {
      return;
    }

    //todo - more colors?
    this.doughnutChartData = {
      labels: this.datasetsLabels,
    datasets: [{
      data: this.createDatasets(),
      backgroundColor: this.datasetsColors,
      hoverBackgroundColor: [...this.datasetsColors.map(color => lighterColor(color, 50))],
      hoverBorderWidth: 0,
    }]};

    this.updateDropdownData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (JSON.stringify(changes['cardData'].previousValue) !== JSON.stringify(changes['cardData'].currentValue)) {
      this.renderData()
    }
  }

  public createDatasets(): number[] {
    this.datasetsLabels.length = 0;

    const datasets: number[] = [];

    for (const region of this.getUniqueRegions()) {
      if(!region || region === null) {
        continue;
      }

      this.datasetsLabels.push(region);
      datasets.push(this.regionInstrumentsSum(region) * 100); // 1.00 = 100%
    };

    return datasets;
  }

  private regionInstrumentsSum(region: string): number {
    return this.getRegionInstruments(region).map(instrument => instrument.weight).reduce((partialSum, a) => partialSum + a, 0) ?? 0;
  }

  private getRegionInstruments(region: string): ModelStrategyInstrumentOut[] {
    const regionInstruments = this.cardData?.chartData.filter(instrument => instrument.instrument.regionalOrientations && instrument.instrument.regionalOrientations[0] === region) ?? [];

    if(region === cashRegionName && this.cardData) {
      const cashInstrument = this.cardData.chartData.find(instrument => instrument.instrument.cash)!;

      regionInstruments.push({
        instrument: cashInstrument.instrument,
        weight: cashInstrument.weight,
      });
    }

    return regionInstruments;
  }

  private getUniqueRegions(): Set<string | null> {
    const withoutCash = this.cardData!.chartData.filter(instrument => !instrument.instrument.cash) ?? [];
    const output = new Set(withoutCash.map(inst => inst.instrument.regionalOrientations ? inst.instrument.regionalOrientations[0] : null));

    this.handleCashCategory(output);

    return output;
  }

  public updateDropdownData(): void {
    const dropdownData: InstrumentDropdownInput[] = [];
    this.dropdownData.length = 0;

    if (this.cardData) {
      for (const region of this.getUniqueRegions()) {
        if(region === null || !region) {
          this.loggerService.error('Invalid state - instrument without region in ' + JSON.stringify(this.cardData.chartData));
          continue;
        }

        dropdownData.push(
          {
            categoryName: region,
            instrumentContent: this.getRegionInstruments(region),
          }
        );
      }
    }

    this.dropdownData = [...dropdownData];
  }

  private handleCashCategory(uniqueRegions: Set<string | null>): void {
    if(!uniqueRegions.has(cashRegionName)) {
      uniqueRegions.add(cashRegionName);
    }
  }
}
